// File: admin-auth.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AdminUserInfo {
  userName: string | null;
  email: string | null;
  tenantId: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AdminAuthService {

  private tenantNameSubject = new BehaviorSubject<string | null>(localStorage.getItem('tenant_name'));
  tenantName$ = this.tenantNameSubject.asObservable();

  private adminUserInfoSubject = new BehaviorSubject<AdminUserInfo>({
    userName: localStorage.getItem('admin_user_name'),
    email: localStorage.getItem('admin_user_email'),
    tenantId: localStorage.getItem('admin_tenant_id')
  });
  adminUserInfo$ = this.adminUserInfoSubject.asObservable();

  private loggedIn = new BehaviorSubject<boolean>(localStorage.getItem('admin_logged_in') === 'true');
  public loggedIn$ = this.loggedIn.asObservable();




  constructor() {
    const hasToken = !!localStorage.getItem('x-id-token');

    if (hasToken) {
      this.resolvePlatformAdmin();
      this.resolveTenantAdmin();
    }

  }
  login(userName: string, email: string, tenantId: string): void {
    localStorage.setItem('admin_logged_in', 'true');
    localStorage.setItem('admin_user_name', userName);
    localStorage.setItem('admin_user_email', email);
    localStorage.setItem('admin_tenant_id', tenantId);

    this.adminUserInfoSubject.next({ userName, email, tenantId });
    this.loggedIn.next(true);
  }

  logout(): void {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_user_name');
    localStorage.removeItem('admin_user_email');
    localStorage.removeItem('admin_tenant_id');
    localStorage.removeItem('tenant_name');
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_user_sub');
    localStorage.clear();


    this.adminUserInfoSubject.next({ userName: null, email: null, tenantId: null });
    this.tenantNameSubject.next(null);
    this.loggedIn.next(false);
  }

  setAdminUserInfo(userName: string, email: string, tenantId: string): void {
    this.adminUserInfoSubject.next({ userName, email, tenantId });
  }

  setTenantName(name: string | null): void {
    localStorage.setItem('tenant_name', name ?? '');
    this.tenantNameSubject.next(name);
  }

  refreshUserInfoFromStorage(): void {
    this.adminUserInfoSubject.next({
      userName: localStorage.getItem('admin_user_name'),
      email: localStorage.getItem('admin_user_email'),
      tenantId: localStorage.getItem('admin_tenant_id')
    });
  }

  isLoggedIn(): boolean {
    return this.loggedIn.value === true;
  }

  private platformAdminSubject = new BehaviorSubject<boolean>(false);
  private tenantAdminSubject = new BehaviorSubject<boolean>(false);
  platformAdmin$ = this.platformAdminSubject.asObservable();
  tenantAdmin$ = this.tenantAdminSubject.asObservable();

  async resolvePlatformAdmin(): Promise<void> {
    const isAdmin = await this.isPlatformAdmin();
    this.platformAdminSubject.next(isAdmin);
  }

  async resolveTenantAdmin(): Promise<void> {
    const isTenantAdmin = await this.isTenantAdmin();
    this.tenantAdminSubject.next(isTenantAdmin);
  }

  isTenantAdminSync(): boolean {
    return this.tenantAdminSubject.value === true;
  }

  isPlatformAdminSync(): boolean {
    return this.platformAdminSubject.value === true;
  }

  async isTenantAdmin(): Promise<boolean> {
    const groups = await this.getIdTokenGroups();
    return groups.includes('tenant_admin');
  }

  async isPlatformAdmin(): Promise<boolean> {
    const groups = await this.getIdTokenGroups();
    return groups.includes('toob_admin');
  }

  private async getIdTokenGroups(): Promise<string[]> {
    const token = await this.getIdToken();
    if (!token) return [];

    const payload = await this.decodeJwtPayload(token);
    const groups = payload?.['cognito:groups'];

    return Array.isArray(groups) ? groups : [];
  }

  private async getIdToken(): Promise<string | null> {
    return localStorage.getItem('x-id-token');
  }

  private decodeJwtPayload(token: string): any {
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

}
